import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MoneyRecive, PercentageCircle, WalletMinus } from "iconsax-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { GenericStatCard } from "@/components/Molecules";
import { toAmount } from "@/lib/billing/constants";
import { formatPersianNumber } from "@/lib/format/persian";
import type { BillingStatsRowProps } from "./BillingStatsRow.types";
import * as styles from "./BillingStatsRow.styles";

export type { BillingStatsRowProps } from "./BillingStatsRow.types";

export const BillingStatsRow = memo(function BillingStatsRow({
  summary,
  isLoading = false,
}: BillingStatsRowProps) {
  const { t } = useTranslation("billing");
  const theme = useTheme();

  const totalSales = formatPersianNumber(toAmount(summary?.total_sales));
  const totalCommission = formatPersianNumber(toAmount(summary?.total_commission));
  const debt = toAmount(summary?.outstanding_debt);
  const debtColor = debt > 0 ? theme.palette.error.main : theme.palette.success.main;

  const withCurrency = (value: string, currencySx: typeof styles.desktopMonthlyCurrency) => (
    <>
      {value}{" "}
      <Typography component="span" sx={currencySx}>
        {t("stats.currency")}
      </Typography>
    </>
  );

  return (
    <>
      <Box sx={styles.desktopGrid}>
        <GenericStatCard
          value={withCurrency(totalSales, styles.desktopMonthlyCurrency)}
          label={t("stats.totalSales")}
          icon={<MoneyRecive size={19} variant="Bold" color={theme.palette.primary.main} />}
          accentColor={theme.palette.primary.main}
          isLoading={isLoading}
        />
        <GenericStatCard
          value={withCurrency(totalCommission, styles.desktopMonthlyCurrency)}
          label={t("stats.totalCommission")}
          icon={<PercentageCircle size={19} variant="Bold" color={theme.palette.warning.main} />}
          accentColor={theme.palette.warning.main}
          isLoading={isLoading}
        />
        <GenericStatCard
          value={withCurrency(formatPersianNumber(debt), styles.desktopMonthlyCurrency)}
          label={t("stats.outstandingDebt")}
          icon={<WalletMinus size={19} variant="Bold" color={debtColor} />}
          accentColor={debtColor}
          isLoading={isLoading}
        />
      </Box>

      <Box sx={styles.mobileTopGrid}>
        <GenericStatCard
          value={totalCommission}
          label={t("stats.totalCommission")}
          icon={<PercentageCircle size={17} variant="Bold" color={theme.palette.warning.main} />}
          accentColor={theme.palette.warning.main}
          isLoading={isLoading}
        />
        <GenericStatCard
          value={formatPersianNumber(debt)}
          label={t("stats.outstandingDebtMobile")}
          icon={<WalletMinus size={17} variant="Bold" color={debtColor} />}
          accentColor={debtColor}
          isLoading={isLoading}
        />
      </Box>

      <Box sx={styles.mobileMonthlyCard}>
        <Typography sx={styles.mobileMonthlyLabel}>{t("stats.totalSales")}</Typography>
        <Typography sx={styles.mobileMonthlyValue}>
          {isLoading ? "—" : withCurrency(totalSales, styles.mobileMonthlyCurrency)}
        </Typography>
      </Box>
    </>
  );
});
